import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const keyToName = {
  warehoused: `Принято`,
  shipped: `Отгружено`,
  available: `Остаток`,
};

const rangeToDataName = {
  week: `weeklyData`,
  year: `annuallyData`,
};

const Wrapper = styled(ResponsiveContainer)`
  .recharts-wrapper .recharts-cartesian-grid-vertical line:nth-last-of-type(-n + 2) {
    stroke-opacity: 0;
  }

  .recharts-tooltip-item {
    padding: 0 !important;
    margin-top: 2px;
  }

  .recharts-tooltip-item:nth-of-type(2) {
    color: #98a6bd !important;
  }
`;

function BarChartCard(props) {
  const {
    title,
    thisWeekSales,
    previousWeekSales,
    previousWeekPositiveDelta,
    previousMonthSales,
    previousMonthPositiveDelta,
    keyToLabel,
    formatNumber,
  } = props;

  const [range, setRange] = useState(`week`);

  const { t } = useAutoTranslation();

  return (
    <div className="col-xl-4">
      <div className="card">
        <div className="card-body">
          <div className="float-right d-none d-md-inline-block">
            <div className="btn-group mb-2">
              <button
                type="button"
                className={`btn btn-xs ${range === `week` ? `btn-secondary` : `btn-light`}`}
                onClick={() => setRange(`week`)}
              >
                {t('Неделя')}
              </button>
              <button
                type="button"
                className={`btn btn-xs ${range === `year` ? `btn-secondary` : `btn-light`}`}
                onClick={() => setRange(`year`)}
              >
                {t('Год')}
              </button>
            </div>
          </div>
          <h4 className="header-title">{title}</h4>
          <div className="row mt-4 text-center">
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Отгрузки за неделю')}</p>
              <h4>
                {formatNumber(thisWeekSales.toLocaleString())} {t('шт')}
              </h4>
            </div>
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Прошлая неделя')}</p>
              <h4>
                <i
                  className={`${
                    previousWeekPositiveDelta
                      ? `fe-arrow-up text-success`
                      : `fe-arrow-down text-danger`
                  } mr-1`}
                />
                {formatNumber(previousWeekSales.toLocaleString())} {t('шт')}
              </h4>
            </div>
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Прошлый месяц')}</p>
              <h4>
                <i
                  className={`${
                    previousMonthPositiveDelta
                      ? `fe-arrow-up text-success`
                      : `fe-arrow-down text-danger`
                  } mr-1`}
                />
                {formatNumber(previousMonthSales.toLocaleString())} шт
              </h4>
            </div>
          </div>
          <div className="mt-3">
            <Wrapper width="100%" height={300}>
              <ComposedChart
                data={props[rangeToDataName[range]]}
                margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
              >
                <XAxis dataKey="point" axisLine={false} tickLine={false} />
                <YAxis
                  width={45}
                  domain={[() => 0, (dataMax) => Math.round(dataMax * 1.15)]}
                  tickCount={7}
                  strokeWidth={1}
                  axisLine={{ stroke: `#c3c3c3` }}
                  tickLine={false}
                  tick={{ fill: `#98a6ad` }}
                  tickFormatter={(number) => formatNumber(number)}
                  yAxisId="left"
                />
                <YAxis
                  width={45}
                  domain={[() => 0, (dataMax) => Math.round(dataMax * 1.15)]}
                  tickCount={7}
                  strokeWidth={1}
                  axisLine={{ stroke: `#f7b84b` }}
                  tickLine={false}
                  tick={{ fill: `#f7b84b` }}
                  tickFormatter={(number) => formatNumber(number)}
                  yAxisId="right"
                  orientation="right"
                />
                <CartesianGrid horizontal={false} stroke="#f5f5f5" strokeWidth={1} active={false} />
                <Tooltip
                  cursor={false}
                  formatter={(value, key) => [`${value} шт`, keyToName[key]]}
                  labelFormatter={(key) => keyToLabel[key]}
                  contentStyle={{
                    border: `none`,
                    boxShadow: `0 1px 4px 0 rgba(0, 0, 0, 0.1)`,
                    borderRadius: `0.25rem`,
                    opacity: `0.95`,
                    fontWeight: 500,
                    backdropFilter: `blur(2px)`,
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 5 }}
                />
                <Bar yAxisId="left" dataKey="warehoused" fill="#7e57c2" barSize={8} />
                <Bar yAxisId="left" dataKey="shipped" fill="#e3eaef" barSize={8} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="available"
                  stroke="#f7b84b"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={{
                    stroke: `#f7b84b`,
                    strokeWidth: 1,
                    strokeDasharray: 0,
                  }}
                  activeDot={false}
                />
              </ComposedChart>
            </Wrapper>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BarChartCard;
